import type { ColumnCatalogDef } from '@/types/column-catalog'
import { areDataTypesCompatible } from './filter-entity'

/**
 * Measurement-domain bucket — finer-grained than `dataType`.
 *
 * Two columns are comparable iff they share a `columnType` (default rule) or a
 * column opts a peer in via its `comparesTo` list. Mirrors the legacy app's
 * `columnType` concept (small-dollar `price` vs big-dollar `largePrice`, etc.)
 * so the new code can refuse semantically nonsense pairings like
 * `total_news_count > market_cap` even though both have JS-numeric `dataType`.
 */
export const COLUMN_TYPES = [
  // Numeric domains
  'price', // small dollar amounts (deltas, last price)
  'largePrice', // big dollar amounts (market cap, dollar volume)
  'volume', // trading volume / share counts in flow
  'largeInteger', // generic big-int (legacy; superseded by tradeCount/shareCount below)
  'tradeCount', // discrete count of trades / ticks
  'shareCount', // discrete share counts (shares outstanding, float)
  'percentage',
  'expression', // user-defined expressions — comparable to any numeric column
  'arbitraryNumber', // miscellaneous counts — default-incomparable, opt-in via comparesTo
  // Non-numeric coarse buckets (default-incomparable — see DEFAULT_INCOMPARABLE)
  'string',
  'enum',
  'boolean',
  'date',
  // Non-numeric sub-domains: same-bucket comparable, distinct from coarse parents.
  'string_time', // wall-clock time strings (e.g. day0_high_time, day0_low_time)
  'enum_dilution', // dilution-risk enum family
] as const
export type ColumnType = (typeof COLUMN_TYPES)[number]

/**
 * Numeric `columnType`s an expression is comparable with — mirrors the old
 * site's `COLUMN_TYPES.expression.comparesTo` list. The synthetic columnMap
 * entry for a panel expression sets its `comparesTo` to a copy of this list
 * so that picking a numeric LHS surfaces expressions in the RHS picker, and
 * picking an expression LHS surfaces numeric columns. Symmetry is enforced
 * by `areColumnsCompatible` walking both directions.
 *
 * Frozen so callers can't accidentally mutate the shared list. Copy with
 * `[...EXPRESSION_COMPARES_TO]` to satisfy ColumnCatalogDef's mutable
 * `comparesTo: string[]` field.
 */
export const EXPRESSION_COMPARES_TO: readonly string[] = Object.freeze([
  'columnType.expression',
  'columnType.price',
  'columnType.largePrice',
  'columnType.volume',
  'columnType.largeInteger',
  'columnType.tradeCount',
  'columnType.shareCount',
  'columnType.percentage',
])

/**
 * Coarse buckets that should default to incomparable. A column tagged with one
 * of these has no implicit reflexive match — pairs are opt-in only via
 * `comparesTo` or by tagging both columns with a finer sub-domain
 * (e.g. `enum_dilution`, `string_time`). Mirrors the legacy app, where each
 * enum/string column is its own measurement domain unless explicitly paired.
 */
export const DEFAULT_INCOMPARABLE: ReadonlySet<ColumnType> = new Set<ColumnType>([
  'arbitraryNumber',
  'enum',
  'string',
  'boolean',
])

/** Finer-than-dataType domains; never satisfied by a dataType-only fallback. */
export const SUBDOMAIN_TYPES: ReadonlySet<ColumnType> = new Set<ColumnType>([
  'string_time',
  'enum_dilution',
])

/**
 * Walk `from.comparesTo` (or its default rule) and return whether any entry
 * accepts `to`. Internal — `areColumnsCompatible` calls this in both directions
 * so that a one-sided `comparesTo` opt-in is symmetric (if A says it pairs
 * with B, then B pairs with A regardless of B's own `comparesTo`).
 */
function comparesToMatches(from: ColumnCatalogDef, to: ColumnCatalogDef): boolean {
  // Coarse buckets (`arbitraryNumber`, `enum`, `string`, `boolean`) have no
  // implicit reflexive match — two misc-count / cross-domain enum / cross-
  // domain string columns are presumed unrelated until explicitly opted in
  // via `comparesTo` or via a shared sub-domain tag (e.g. `enum_dilution`).
  const defaultRule =
    from.columnType && DEFAULT_INCOMPARABLE.has(from.columnType)
      ? []
      : [`columnType.${from.columnType}`]
  const compares = from.comparesTo ?? defaultRule
  for (const entry of compares) {
    const dot = entry.indexOf('.')
    if (dot < 0) continue
    const field = entry.slice(0, dot)
    const value = entry.slice(dot + 1)
    if (field === 'columnType' && to.columnType === value) return true
    if (field === 'name' && to.key === value) return true
  }
  return false
}

/**
 * Decide whether `lhs` can be compared (column-to-column) against `rhs`.
 *
 * - Same column on both sides → never comparable.
 * - When either side is missing `columnType`, fall back to a strict `dataType`
 *   match (legacy parity for not-yet-tagged catalogs).
 * - Otherwise the relation is symmetric: accept if EITHER side's `comparesTo`
 *   (or its default `[columnType.<self>]` rule) matches the other. This means
 *   a column author can declare a one-sided `comparesTo: ['name.<peer>']`
 *   without having to mirror the entry on the peer — the predicate enforces
 *   symmetry by walking both directions.
 */
export function areColumnsCompatible(lhs: ColumnCatalogDef, rhs: ColumnCatalogDef): boolean {
  if (lhs.key === rhs.key) return false

  if (!lhs.columnType || !rhs.columnType) {
    // A sub-domain tag carries information the dataType cannot: a clock column is
    // `dataType: 'string'` but is not comparable to an untagged string column.
    const tagged = lhs.columnType ?? rhs.columnType
    if (tagged && SUBDOMAIN_TYPES.has(tagged)) return false
    return areDataTypesCompatible(lhs.dataType, rhs.dataType)
  }

  return comparesToMatches(lhs, rhs) || comparesToMatches(rhs, lhs)
}
